/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useContext, useEffect } from 'react';
import { Badge } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import TruncateText from '../TruncatedText/TruncateText';
import {
  IBreakdownByMeterProps,
  IDataTableHeader,
  IRowidObj
} from './BreakdownByMeterProps';
import './DataTableCommonTheme.scss';
import {
  getNullConverted,
  getNullConvertedValueForEnergyCardTable
} from '../Utils/EnergyUsagesViewDetailsUtils';
import { ApiContext } from '../../../context/ApiContext';
import {
  ECartAt,
  IAny
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  MAGIC_VALUE,
  PERCENT_CHANGE_THRESHOLD
} from '../Utils/Constants';
import { getPercentageColor } from '../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';
import { ConversionType } from '../Utils/unitHandling/UnitModel';

const BreakdownByMeter = (props: IBreakdownByMeterProps) => {
  const { meterData, columnHeaders, unit, onSorting } = props;
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>('high');

  const apiContext = useContext(ApiContext);
  const { onPortfolioSiteClick, apiCallAt, isBreakdownEquipment } = apiContext;

  const onSiteclick = (data: IRowidObj) => {
    if (data && data.id) {
      if (onPortfolioSiteClick) {
        onPortfolioSiteClick(data.id);
      }
    }
  };
  var totalDisAggregated = meterData.reduce(
    (accum: number, val: any) => accum + Math.abs(val.high),
    0
  );
  const getEuiElements = (value: any, unit: string) => {
    return (
      <div className="saving-value-cell">
        <span className="table-cell-common emass-siteview-table-cell">
          {Math.abs(value)} {unit}{' '}
          {Math.abs(value) == 0
            ? `(0%)`
            : `(${Math.abs(
                (Math.abs(value) * 100) / totalDisAggregated
              ).toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)}%)`}
        </span>
      </div>
    );
  };

  const getChangedFieldValue = (value: any, rowData: any) => {
    const perChange = Math.abs(rowData.totalDeviationPercentage);
    const changeValue: IAny = getConvertedValue(
      rowData.conversionType || ConversionType.ELECTRICITY,
      props.energyMeasurementUnit ? props.energyMeasurementUnit : '',
      rowData.totalDeviation,
      rowData.deviationUnit
    ).Value;

    return (
      <div className="saving-value-cell ">
        {value === null ||
        (apiContext.showChangeWhenData &&
          (perChange > PERCENT_CHANGE_THRESHOLD ||
            value.toString() === Number(0).toString())) ? (
          '--'
        ) : (
          <span className="table-cell-common emass-siteview-poitive-table-cell">
            <span
              style={{
                color: getPercentageColor(rowData.totalDeviationPercentage)
              }}
            >
              {Math.abs(changeValue)} {rowData.deviationUnit}
              {`(${Math.abs(rowData.totalDeviationPercentage)}%)`}
            </span>
          </span>
        )}
      </div>
    );
  };

  const cellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;

    if (field === 'type') {
      return (
        <>
          {apiCallAt == ECartAt.portfolioLabel ? (
            <div
              className="savings-badge emaas-site-summary"
              onClick={() => onSiteclick(rowData)}
            >
              {rowData.totalDeviation < 0 && <Badge color="red" empty />}{' '}
              <TruncateText text={value} />
            </div>
          ) : (
            <div className="savings-badge table-cell-common emass-siteview-table-cell">
              {rowData.totalDeviation < 0 && <Badge color="red" empty />}{' '}
              <TruncateText text={value} />
            </div>
          )}
        </>
      );
    }
    if (isBreakdownEquipment) {
      if (['eui'].includes(field)) {
        return getEuiElements(value, rowData.unit);
      } else if (['high'].includes(field)) {
        const energyValue: IAny = getConvertedValue(
          rowData.conversionType || ConversionType.ELECTRICITY,
          props.energyMeasurementUnit ? props.energyMeasurementUnit : '',
          rowData.high,
          rowData.unit
        ).Value;

        return (
          <div className="saving-value-cell">
            <span className="table-cell-common emass-siteview-table-cell">
              {getNullConvertedValueForEnergyCardTable(value) !== '--'
                ? getNullConvertedValueForEnergyCardTable(energyValue) +
                  ' ' +
                  rowData.unit
                : '--'}
            </span>
          </div>
        );
      }
      // if (field === 'high' || field === 'eui') {
      //   return getEuiElements(value);
      // }
    } else {
      if (['high'].includes(field)) {
        const energyValue: IAny = getConvertedValue(
          rowData.conversionType || ConversionType.ELECTRICITY,
          props.energyMeasurementUnit ? props.energyMeasurementUnit : '',
          rowData.high,
          rowData.unit
        ).Value;

        return (
          <div className="saving-value-cell">
            <span className="table-cell-common emass-siteview-table-cell">
              {getNullConvertedValueForEnergyCardTable(value) !== '--'
                ? getNullConvertedValueForEnergyCardTable(energyValue) +
                  ' ' +
                  rowData.unit
                : '--'}
            </span>
          </div>
        );
      } else if (['eui'].includes(field)) {
        return (
          <div className="saving-value-cell">
            <span className="table-cell-common emass-siteview-table-cell">
              {getNullConvertedValueForEnergyCardTable(value)}
            </span>
          </div>
        );
      }
      // if (field === 'high' || field === 'eui') {
      //   return (
      //     <div className="saving-value-cell">
      //       <span className="table-cell-common emass-siteview-table-cell">
      //         {getNullConverted(value)}
      //       </span>
      //     </div>
      //   );
      // }
    }
    if (field === 'change') {
      return getChangedFieldValue(value, rowData);
      // return (
      //   <div className="saving-value-cell ">
      //     {value === null ? (
      //       '--'
      //     ) : (
      //       <span className="table-cell-common emass-siteview-poitive-table-cell">
      //         <span
      //           style={{
      //             color: getPercentageColor(rowData.totalDeviationPercentage)
      //           }}
      //         >
      //           {Math.abs(value)}{' '}
      //           {`(${Math.abs(rowData.totalDeviationPercentage)}%)`}
      //         </span>
      //       </span>
      //     )}
      //   </div>
      // );
    }
    return <></>;
  };

  return (
    <div className="emaas-breakdown-meter-table-container">
      <DataTable
        data={meterData}
        className="style-tableContent"
        onSort={({ sortField, sortOrder }) => {
          setFieldSortOrder(sortOrder as number);
          setSortFieldName(sortField);
          if (sortField === 'high' && onSorting) {
            onSorting(sortOrder as number);
          }
        }}
        sortField={sortFieldName}
        sortOrder={fieldSortOrder}
        rows={MAGIC_VALUE.FIVE}
      >
        {columnHeaders.map((header: IDataTableHeader) => (
          <DataTable.Column
            key={header.toString()}
            field={header.Field}
            header={header.HeaderValue}
            renderer={cellDataRenderer}
            align={header.TextAlignValue}
            sortable={header.sortable}
          />
        ))}
      </DataTable>
    </div>
  );
};

export default BreakdownByMeter;
